import React, { useState, useEffect, useRef } from 'react';
import { Box, Stack, Button, Typography, AppBar, ClickAwayListener } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DelegonMainLogoWhite from '../../../assets/Delegon_Main_Logo_WHITE.png';

import { paths, ROOT_PATHS } from 'routes';
import { IconWrapper } from 'components/Icon';
import { MobileMenu } from 'website/components/MobileMenu';
import { BuyerSearch } from 'website/components/BuyerSearch';
import { LocalizedLink } from 'website/components/LocalizedLink';
import { LanguageSwitcher } from 'website/components/LanguageSwitcher';

export const Menu = ({ websiteContext = 'suppliers' }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const lastSlashIndex = location.pathname.lastIndexOf('/');
  const dynamicPart = lastSlashIndex !== -1 ? location.pathname.substring(lastSlashIndex + 1) : 'website';
  const [selected, setSelected] = useState(dynamicPart);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const body = document.body;
  const originalOverflow = useRef(body.style.overflow);
  const homeURL = websiteContext === 'buyers' ? ROOT_PATHS.WEBSITE_BUYERS : ROOT_PATHS.WEBSITE;
  const registerURL = websiteContext === 'buyers' ? paths.auth.buyersRegister : paths.auth.register;
  const pricingURL = websiteContext === 'buyers' ? paths.website.buyersPricing : paths.website.pricing;

  useEffect(() => {
    const pathname = location.pathname;
    const pathSegments = pathname.split('/').filter((segment) => segment !== '');

    if (pathSegments.length > 2 && pathSegments[pathSegments.length - 2] === 'blog') {
      setSelected('blog');
    } else if (pathSegments.length > 1 && pathSegments[pathSegments.length - 1] === 'buyers') {
      setSelected('website');
    } else {
      setSelected(dynamicPart);
    }
  }, [dynamicPart]);

  const menuItems = [
    { label: t('website:common.nav.homepage'), value: 'website', path: homeURL },
    { label: t('website:common.nav.plus'), value: 'plus', path: paths.website.plus },
    { label: t('website:common.nav.blog'), value: 'blog', path: paths.website.blog },
    { label: t('website:common.nav.pricing'), value: 'pricing', path: pricingURL },
    { label: t('website:common.nav.jobs'), value: 'jobs', path: paths.website.jobAds },
    { label: t('website:common.nav.contactUs'), value: 'contact-us', path: paths.website.contactUs },
  ];

  const handleOpen = () => {
    setOpen(true);
    originalOverflow.current = body.style.overflow;
    body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setOpen(false);
    body.style.overflow = originalOverflow.current;
  };

  const handleSearchOpen = () => {
    setSearchOpen((prev) => !prev);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
  };

  return (
    <AppBar
      sx={{
        height: 70,
        py: 1.25,
        width: 1,
        maxWidth: '100vw',
        backgroundColor: 'background.primary',
        position: 'fixed',
        top: 0,
      }}
    >
      <Stack
        display={'flex'}
        direction={'row'}
        sx={{
          height: 1,
          alignItems: 'center',
          mx: { xs: 4, md: 6 },
          justifyContent: 'space-between',
        }}
      >
        <Box
          component={LocalizedLink}
          to={homeURL}
        >
          <img
            className="small-logo"
            src={DelegonMainLogoWhite}
            alt={'Delegon'}
            loading="lazy"
            style={{
              width: 150,
              flexShrink: 0,
            }}
          />
        </Box>
        <Stack
          display={{ xs: 'none', md: 'flex' }}
          gap={{md: 3.75, lg: 4.5}}
          flexDirection={'row'}
          sx={{
            '& > div > a': {
              textDecoration: 'none',
              color: '#fff',
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: 1.5,
            },
          }}
        >
          {menuItems.map((item) => (
            <Box
              gap={1}
              key={item.value}
              sx={{
                height: 32,
                position: 'relative',
                '&:hover': {
                  '&::after': {
                    width: '50%',
                  },
                },
              }}
            >
              <Typography
                key={item.value}
                component={LocalizedLink}
                to={item.path}
                state={{ websiteContext: websiteContext }}
                onClick={() => setSelected(item.value)}
              >
                {item.label}
              </Typography>
              <Box
                sx={{
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  height: '2px',
                  width: selected === item.value ? '23px' : '0',
                  backgroundColor: '#fff',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          ))}
        </Stack>
        <Stack
          display={{ xs: 'none', md: 'flex' }}
          gap={1}
          flexDirection={'row'}
          alignItems={'center'}
        >
          <Box sx={{ position: 'relative', alignContent: 'center' }}>
            <IconWrapper
              icon="search"
              onClick={handleSearchOpen}
              sx={{
                cursor: 'pointer',
                color: 'white',
                mr: 2,
                svg: { height: 24, width: 24 },
              }}
            />
          </Box>
          {/* grid (not flex) so both buttons share the width of whichever
              label is longer in the active language, instead of each
              sizing to its own text */}
          <Box
            sx={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: '1fr',
              gap: 1,
              '& > a, .MuiButton-root': {
                height: 34,
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#fff',
                lineHeight: 1.75,
                whiteSpace: 'nowrap',
              },
              '.MuiButton-root': {
                width: 1,
                minWidth: 99,
              },
            }}
          >
            <LocalizedLink to={paths.auth.login} state={{ websiteContext: websiteContext }}>
              <Button
                size="medium"
                variant="contained"
                sx={{
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                {t('website:common.nav.login')}
              </Button>
            </LocalizedLink>
            <LocalizedLink to={registerURL} state={{ websiteContext: websiteContext }}>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                sx={({ palette }) => ({
                  '&:hover': {
                    backgroundColor: palette.primary.main,
                  },
                })}
              >
                {t('website:common.nav.signUp')}
              </Button>
            </LocalizedLink>
          </Box>
          <LanguageSwitcher sx={{ ml: 1, color: '#fff', alignItems: 'center' }} />
        </Stack>
        <Stack display={{ xs: 'flex', md: 'none' }} flexDirection={"row"} gap={1}>
          <Box height={40} alignContent="center">
          <IconWrapper
            icon="search"
            onClick={handleSearchOpen}
            sx={{
              mt: -0.7,
              cursor: 'pointer',
              width: 40,
              height: 40,
              svg: {
                color: 'white',
                stroke: 'white',
                width: 30,
                height: 30,
              },
            }}
          />
          </Box>
          <Box height={40} onClick={handleOpen} sx={{ mr: '-5px' }}>
            <IconWrapper
              icon="bars3"
              sx={{
                height: 40,
                width: 40,
                svg: {
                  color: 'white',
                  stroke: 'white',
                  width: 40,
                  height: 40,
                },
              }}
            />
          </Box>
        </Stack>
        {open && <MobileMenu menuItems={menuItems} onClose={handleClose} />}
        {searchOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              background: 'rgba(0, 0, 0, 0.36)',
              justifyContent: 'center',
              display: searchOpen ? 'flex' : 'none',
              zIndex: 40,
              alignItems: 'flex-start',
              overflow: 'hidden',
            }}
          >
            <ClickAwayListener onClickAway={handleSearchClose}>
              <Box
                sx={{
                  mt: { xs: 2, md: 10 },
                  width: { xs: '90%', md: 1 },
                  maxWidth: '940px',
                  boxSizing: 'border-box',
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'border.jobAd',
                  position: 'relative',
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                  },
                }}
              >
                <BuyerSearch onResultClick={handleSearchClose} />
              </Box>
            </ClickAwayListener>
          </Box>
        )}
      </Stack>
    </AppBar>
  );
};